import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Grid, Chip, CircularProgress } from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockResetIcon from '@mui/icons-material/LockReset';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import KeyIcon from '@mui/icons-material/Key';
import DevicesIcon from '@mui/icons-material/Devices';
import TwoFactorSetupDialog from '../../components/TwoFactorSetupDialog';
import { useAuthStore } from '../../store/authStore';
import { urlBase } from '../../config';

export default function SecuritySettings({ token, onNotify, darkTheme }) {
    const [statusData, setStatusData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);

    const authStore = useAuthStore();
    const twoFactorEnabledFromStore = authStore.twoFactorEnabled;

    const fetchStatus = async () => {
        if (!token) return;
        setLoading(true);
        try {
            const resp = await fetch(`${urlBase}/api/auth/2fa/status`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (resp.ok) {
                const data = await resp.json();
                setStatusData(data);
                authStore.setMfaStatus(data);
            }
        } catch (err) {
            console.error("Błąd podczas pobierania statusu 2FA:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatus();
    }, [token]);

    const isEnabled = statusData?.twoFactorEnabled ?? twoFactorEnabledFromStore;
    const daysRemaining = statusData?.daysRemaining ?? authStore.daysRemaining ?? 0;
    const mfaDeadline = statusData?.mfaDeadline ?? authStore.mfaDeadline;

    const formattedDeadline = mfaDeadline
        ? new Date(mfaDeadline).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : null;

    const handleSetupSuccess = (msg) => {
        fetchStatus();
        if (onNotify) {
            onNotify(msg || 'Uwierzytelnianie dwuskładnikowe (2FA) zostało pomyślnie aktywowane.', 'success');
        }
    };

    if (loading && !statusData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress size={40} />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            {/* Nagłówek sekcji */}
            <Box sx={{ mb: 3.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.8 }}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                            color: '#FFFFFF',
                            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.35)'
                        }}
                    >
                        <ShieldIcon fontSize="small" />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: darkTheme ? '#F8FAFC' : '#0F172A',
                            fontSize: '18px'
                        }}
                    >
                        Bezpieczeństwo i Weryfikacja Dwuetapowa (2FA)
                    </Typography>
                </Box>
                <Typography
                    variant="body2"
                    sx={{ color: darkTheme ? '#94A3B8' : '#64748B', fontSize: '13px', ml: 6 }}
                >
                    Zabezpiecz swoje konto przed nieautoryzowanym dostępem za pomocą aplikacji Google Authenticator.
                </Typography>
            </Box>

            {/* Karta statusu 2FA */}
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: 3,
                    mb: 3,
                    border: '1px solid',
                    borderColor: isEnabled
                        ? (darkTheme ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.3)')
                        : (darkTheme ? 'rgba(245, 158, 11, 0.35)' : '#FDE68A'),
                    backgroundColor: isEnabled
                        ? (darkTheme ? 'rgba(16, 185, 129, 0.04)' : '#F0FDF4')
                        : (darkTheme ? 'rgba(245, 158, 11, 0.05)' : '#FFFBEB'),
                    boxShadow: 'none'
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: isEnabled ? '#10B981' : '#F59E0B',
                                color: '#FFFFFF',
                                flexShrink: 0,
                                mt: 0.3
                            }}
                        >
                            {isEnabled ? <CheckCircleOutlineIcon /> : <WarningAmberIcon />}
                        </Box>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 0.6, flexWrap: 'wrap' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: darkTheme ? '#F8FAFC' : '#0F172A' }}>
                                    Weryfikacja dwuetapowa (Google Authenticator)
                                </Typography>
                                <Chip
                                    label={isEnabled ? "Aktywne (Włączone)" : "Nieaktywne (Wymaga włączenia)"}
                                    color={isEnabled ? "success" : "warning"}
                                    size="small"
                                    sx={{ fontWeight: 600, fontSize: '11px', height: '22px' }}
                                />
                            </Box>
                            <Typography variant="body2" sx={{ color: darkTheme ? '#CBD5E1' : '#475569', fontSize: '13px', lineHeight: 1.5 }}>
                                {isEnabled
                                    ? "Twoje konto jest chronione kodem jednorazowym generowanym w aplikacji uwierzytelniającej."
                                    : "Każdy użytkownik systemu Pelikan ma 30 dni na włączenie weryfikacji dwuetapowej."
                                }
                            </Typography>
                        </Box>
                    </Box>

                    {!isEnabled && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setDialogOpen(true)}
                            sx={{
                                flexShrink: 0,
                                borderRadius: '9px',
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2.8,
                                py: 1,
                                backgroundColor: '#10B981',
                                '&:hover': {
                                    backgroundColor: '#059669'
                                }
                            }}
                        >
                            Włącz weryfikację 2FA
                        </Button>
                    )}
                </Box>

                {/* Informacja gdy 2FA jest już aktywne */}
                {isEnabled && (
                    <Box
                        sx={{
                            mt: 2.5,
                            pt: 2,
                            borderTop: '1px dashed',
                            borderColor: darkTheme ? 'rgba(16, 185, 129, 0.25)' : 'rgba(16, 185, 129, 0.35)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <LockResetIcon sx={{ color: '#10B981', fontSize: 18 }} />
                        <Typography variant="caption" sx={{ color: darkTheme ? '#94A3B8' : '#475569', fontSize: '12.5px' }}>
                            Ze względów bezpieczeństwa weryfikacji 2FA nie można wyłączyć samodzielnie. W przypadku utraty lub wymiany telefonu skontaktuj się z administratorem w celu zresetowania 2FA.
                        </Typography>
                    </Box>
                )}

                {/* Informacja o okresie karencji (jeśli nieaktywne) */}
                {!isEnabled && (
                    <Box
                        sx={{
                            mt: 2.5,
                            pt: 2,
                            borderTop: '1px dashed',
                            borderColor: darkTheme ? 'rgba(255, 255, 255, 0.12)' : '#FCD34D',
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'flex-start', md: 'center' },
                            justifyContent: 'space-between',
                            gap: 1.5
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LockResetIcon sx={{ color: '#D97706', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: darkTheme ? '#FCD34D' : '#92400E', fontWeight: 600, fontSize: '13px' }}>
                                Czas na wdrożenie: pozostało Ci {daysRemaining} dni {formattedDeadline ? `(do ${formattedDeadline})` : ''}
                            </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: darkTheme ? '#94A3B8' : '#78350F', fontSize: '12px' }}>
                            Po tym terminie przy logowaniu wymagane będzie obowiązkowe ustawienie weryfikacji.
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Informacje edukacyjne: Jak to działa */}
            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: 700,
                    color: darkTheme ? '#E2E8F0' : '#1E293B',
                    mb: 1.5,
                    fontSize: '13.5px',
                    letterSpacing: '0.02em'
                }}
            >
                Jak działa weryfikacja dwuetapowa?
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3.5 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: 2.5,
                            border: '1px solid',
                            borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                            backgroundColor: darkTheme ? 'rgba(255, 255, 255, 0.02)' : '#F8FAFC',
                            height: '100%'
                        }}
                    >
                        <Box sx={{ color: '#3B82F6', mb: 1 }}>
                            <SmartphoneIcon />
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '13px', mb: 0.5 }}>
                            1. Pobierz aplikację
                        </Typography>
                        <Typography variant="caption" sx={{ color: darkTheme ? '#94A3B8' : '#64748B', display: 'block', fontSize: '11.5px', lineHeight: 1.4 }}>
                            Zainstaluj Google Authenticator, Microsoft Authenticator lub Authy na swoim smartfonie.
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: 2.5,
                            border: '1px solid',
                            borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                            backgroundColor: darkTheme ? 'rgba(255, 255, 255, 0.02)' : '#F8FAFC',
                            height: '100%'
                        }}
                    >
                        <Box sx={{ color: '#10B981', mb: 1 }}>
                            <QrCode2Icon />
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '13px', mb: 0.5 }}>
                            2. Zeskanuj kod QR
                        </Typography>
                        <Typography variant="caption" sx={{ color: darkTheme ? '#94A3B8' : '#64748B', display: 'block', fontSize: '11.5px', lineHeight: 1.4 }}>
                            Kliknij „Włącz weryfikację 2FA” i zeskanuj wygenerowany kod w aplikacji na telefonie.
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: 2.5,
                            border: '1px solid',
                            borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                            backgroundColor: darkTheme ? 'rgba(255, 255, 255, 0.02)' : '#F8FAFC',
                            height: '100%'
                        }}
                    >
                        <Box sx={{ color: '#8B5CF6', mb: 1 }}>
                            <KeyIcon />
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '13px', mb: 0.5 }}>
                            3. Zapisz kody zapasowe
                        </Typography>
                        <Typography variant="caption" sx={{ color: darkTheme ? '#94A3B8' : '#64748B', display: 'block', fontSize: '11.5px', lineHeight: 1.4 }}>
                            Otrzymasz listę jednorazowych kodów zapasowych, które pozwolą zalogować się w razie braku telefonu.
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: 2.5,
                            border: '1px solid',
                            borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                            backgroundColor: darkTheme ? 'rgba(255, 255, 255, 0.02)' : '#F8FAFC',
                            height: '100%'
                        }}
                    >
                        <Box sx={{ color: '#F59E0B', mb: 1 }}>
                            <DevicesIcon />
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '13px', mb: 0.5 }}>
                            4. Zaufane urządzenia
                        </Typography>
                        <Typography variant="caption" sx={{ color: darkTheme ? '#94A3B8' : '#64748B', display: 'block', fontSize: '11.5px', lineHeight: 1.4 }}>
                            Możesz oznaczyć bieżący komputer jako zaufany na 30 dni – kod TOTP nie będzie wymagany przy każdym logowaniu.
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Dialog konfiguracji 2FA */}
            <TwoFactorSetupDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                token={token}
                onSuccess={handleSetupSuccess}
            />
        </Box>
    );
}
